import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FinanceiroMontadora() {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [lista, setLista] = useState([]);
  const [pagar, setPagar] = useState(null);
  const [formPagar, setFormPagar] = useState({ valor_pago: 0, data_pagamento: "", comprovante: null });

  useEffect(() => {
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
  }, [user]);

  const load = async () => {
    if (!empresaId) return;
    const r = await api.get(`/empresas/${empresaId}/financeiro-montadora`);
    setLista(r.data);
  };

  useEffect(() => { load(); }, [empresaId]);

  const triggerCron = async () => {
    try {
      const r = await api.post("/api/admin/cron/fechamento-montadora/executar").catch(async () =>
        await api.post("/admin/cron/fechamento-montadora/executar"));
      toast.success(`${r.data.fechamentos_criados} fechamento(s) gerado(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enviarPgto = async () => {
    if (!formPagar.valor_pago || !formPagar.data_pagamento) { toast.error("Valor e data obrigatórios"); return; }
    try {
      const fd = new FormData();
      fd.append("valor_pago", String(formPagar.valor_pago));
      fd.append("data_pagamento", formPagar.data_pagamento);
      if (formPagar.comprovante) fd.append("comprovante", formPagar.comprovante);
      await api.post(`/empresas/${empresaId}/financeiro-montadora/${pagar.id}/marcar-pago`, fd);
      toast.success("Pagamento registrado · arquivado");
      setPagar(null); setFormPagar({ valor_pago: 0, data_pagamento: "", comprovante: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const total = lista.reduce((s, x) => s + (x.valor || 0), 0);
  const totalPendente = lista.filter(x => x.status === "pendente").reduce((s, x) => s + (x.valor || 0), 0);
  const totalPago = lista.filter(x => x.status === "pago").reduce((s, x) => s + (x.valor_pago || 0), 0);

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap">Financeiro · Empresa</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Financeiro da Montadora</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Fechamento mensal automático (dia 1 → último dia) · pagamento até dia 15 do mês seguinte.
          </p>
        </div>
        {user?.role === "admin" && (
          <Button onClick={triggerCron} variant="outline" className="btn-sharp" data-testid="fm-cron">
            Executar fechamento agora
          </Button>
        )}
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="max-w-md" data-testid="fm-empresa"><SelectValue placeholder="Selecione empresa" /></SelectTrigger>
            <SelectContent>{empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-6">
        <DashCard label="Total" v={total} color="text-foreground" />
        <DashCard label="Pendente" v={totalPendente} color="text-[#FFCC00]" />
        <DashCard label="Pago" v={totalPago} color="text-[#00A859]" />
      </div>

      <div className="border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F4F5] border-b border-border">
            <tr>
              <th className="text-left p-3 label-cap">Montadora</th>
              <th className="text-left p-3 label-cap">PIX</th>
              <th className="text-left p-3 label-cap">Período</th>
              <th className="text-left p-3 label-cap">Atend.</th>
              <th className="text-left p-3 label-cap">Valor</th>
              <th className="text-left p-3 label-cap">Vencimento</th>
              <th className="text-left p-3 label-cap">Status</th>
              <th className="text-left p-3 label-cap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={8} className="p-12 text-center text-muted-foreground">Nenhum fechamento</td></tr>}
            {lista.map(f => (
              <tr key={f.id} className="border-b border-border last:border-0" data-testid={`fm-${f.id}`}>
                <td className="p-3 font-medium">{f.montadora_nome}</td>
                <td className="p-3 font-mono text-xs">{f.montadora_pix || "—"}</td>
                <td className="p-3 font-mono text-xs">{f.periodo}</td>
                <td className="p-3 font-mono">{f.qtd_atendimentos}</td>
                <td className="p-3 font-mono">R$ {f.valor.toFixed(2)}</td>
                <td className="p-3 text-xs">{f.vencimento}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase ${f.status === "pago" ? "bg-[#00A859] text-white" : "bg-[#FFCC00]"}`}>
                    {f.status}
                  </span>
                </td>
                <td className="p-3">
                  {f.status === "pendente" && (
                    <Button size="sm" className="btn-sharp h-7 text-[10px] brand-bg text-white"
                      onClick={() => { setPagar(f); setFormPagar({ valor_pago: f.valor, data_pagamento: new Date().toISOString().slice(0,10), comprovante: null }); }}
                      data-testid={`fm-pagar-${f.id}`}>Marcar Pago</Button>
                  )}
                  {f.status === "pago" && f.comprovante_path && (
                    <span className="text-[10px] text-[#00A859]">✓ Arquivado</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!pagar} onOpenChange={(o) => !o && setPagar(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marcar como Pago · {pagar?.montadora_nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Período {pagar?.periodo} · {pagar?.qtd_atendimentos} atendimentos · R$ {pagar?.valor?.toFixed(2)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Valor pago</Label>
                <Input type="number" step="0.01" value={formPagar.valor_pago}
                  onChange={(e) => setFormPagar({ ...formPagar, valor_pago: parseFloat(e.target.value) || 0 })}
                  data-testid="fm-valor" />
              </div>
              <div>
                <Label className="text-xs">Data</Label>
                <Input type="date" value={formPagar.data_pagamento}
                  onChange={(e) => setFormPagar({ ...formPagar, data_pagamento: e.target.value })}
                  data-testid="fm-data" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Comprovante (PDF/JPG/PNG)</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.jfif,.png,.webp,.heic"
                onChange={(e) => setFormPagar({ ...formPagar, comprovante: e.target.files?.[0] })}
                data-testid="fm-comprovante" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagar(null)}>Cancelar</Button>
            <Button onClick={enviarPgto} className="brand-bg text-white" data-testid="fm-confirmar">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DashCard({ label, v, color }) {
  return (
    <div className="border border-border bg-white p-4">
      <div className="label-cap">{label}</div>
      <div className={`font-heading font-extrabold text-2xl mt-1 ${color}`}>
        R$ {(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
      </div>
    </div>
  );
}
